"use client";
import { useEffect, useState, Suspense } from "react";
import { Timestamp } from "firebase/firestore";
import { useAuth } from "@/context/AuthContext";
import { subscribeToOrders } from "@/firebase/firestoreServices";
import type { Order } from "@/models/order";
import { useRouter } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { Bike, Package, ChevronLeft, ChevronRight, Clock } from "lucide-react";

// Statuses that count as "active"
const ACTIVE_STATUSES = new Set([
    "placed", "Pending", "Confirmed", "confirmed",
    "Preparing", "preparing", "Out for Delivery", "out_for_delivery",
]);

const statusColors: Record<string, { bg: string; text: string; label: string }> = {
    placed: { bg: "bg-yellow-100 dark:bg-yellow-900/30", text: "text-yellow-700 dark:text-yellow-300", label: "Placed" },
    Pending: { bg: "bg-yellow-100 dark:bg-yellow-900/30", text: "text-yellow-700 dark:text-yellow-300", label: "Pending" },
    Confirmed: { bg: "bg-blue-100 dark:bg-blue-900/30", text: "text-blue-700 dark:text-blue-300", label: "Confirmed" },
    confirmed: { bg: "bg-blue-100 dark:bg-blue-900/30", text: "text-blue-700 dark:text-blue-300", label: "Confirmed" },
    Preparing: { bg: "bg-orange-100 dark:bg-orange-900/30", text: "text-orange-700 dark:text-orange-300", label: "Preparing" },
    preparing: { bg: "bg-orange-100 dark:bg-orange-900/30", text: "text-orange-700 dark:text-orange-300", label: "Preparing" },
    "Out for Delivery": { bg: "bg-primary/10", text: "text-primary", label: "Out for Delivery" },
    out_for_delivery: { bg: "bg-primary/10", text: "text-primary", label: "Out for Delivery" },
};

// Progress steps
const progressSteps = ["Placed", "Confirmed", "Preparing", "Out for Delivery", "Delivered"];
function getStepIndex(status: string) {
    const map: Record<string, number> = {
        placed: 0, Pending: 0,
        Confirmed: 1, confirmed: 1,
        Preparing: 2, preparing: 2,
        "Out for Delivery": 3, out_for_delivery: 3,
        Delivered: 4, delivered: 4,
    };
    return map[status] ?? 0;
}

const ActiveOrdersContent = () => {
    const { user } = useAuth();
    const router = useRouter();
    const [orders, setOrders] = useState<Order[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!user) { setLoading(false); return; }
        const unsub = subscribeToOrders(user.uid, (all) => {
            setOrders(all.filter((o) => ACTIVE_STATUSES.has(o.status)));
            setLoading(false);
        });
        return () => unsub();
    }, [user]);

    return (
        <div className="min-h-screen bg-muted/30 pb-24">
            {/* Header */}
            <div className="bg-card border-b border-border sticky top-0 z-30">
                <div className="container max-w-2xl mx-auto flex items-center gap-3 px-4 py-4">
                    <button
                        onClick={() => router.back()}
                        className="p-1.5 rounded-full hover:bg-muted transition-colors"
                        aria-label="Back" title="Back"
                    >
                        <ChevronLeft className="w-5 h-5 text-foreground" />
                    </button>
                    <h1 className="text-lg font-bold text-foreground">Active Orders</h1>
                </div>
            </div>

            <div className="container max-w-2xl mx-auto px-4 pt-4 space-y-4">
                {loading ? (
                    <div className="flex items-center justify-center py-20 min-h-[50vh]">
                        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : orders.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
                        <Package className="w-16 h-16 text-muted-foreground/40" />
                        <p className="text-lg font-bold text-foreground">No Active Orders</p>
                        <p className="text-sm text-muted-foreground">Your current orders will appear here.</p>
                        <button
                            onClick={() => router.push("/account")}
                            className="px-8 py-2.5 bg-primary text-white rounded-full font-semibold text-sm"
                        >
                            Start Shopping
                        </button>
                    </div>
                ) : (
                    <AnimatePresence>
                        {orders.map((order, i) => {
                            const foodItemsArr = order.foodItems
                                ? Object.values(order.foodItems)
                                : [];
                            const stepIdx = getStepIndex(order.status);
                            const sc = statusColors[order.status];
                            const orderTime = (order.time instanceof Timestamp)
                                ? order.time.toDate().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
                                : "";

                            return (
                                <motion.div
                                    key={order.id}
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -12 }}
                                    transition={{ delay: i * 0.06 }}
                                    className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden"
                                >
                                    {/* ── Top bar ── */}
                                    <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-muted/30">
                                        <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                                            {order.type === "Delivery"
                                                ? <Bike className="w-4 h-4 text-primary" />
                                                : <Package className="w-4 h-4 text-primary" />
                                            }
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-bold text-foreground truncate">
                                                Order #{order.orderId || order.id.slice(-6).toUpperCase()}
                                            </p>
                                            {orderTime && (
                                                <p className="text-xs text-muted-foreground flex items-center gap-1">
                                                    <Clock className="w-3 h-3" /> {orderTime}
                                                </p>
                                            )}
                                        </div>
                                        {sc && (
                                            <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${sc.bg} ${sc.text}`}>
                                                {sc.label}
                                            </span>
                                        )}
                                    </div>

                                    {/* ── Progress bar ── */}
                                    <div className="px-4 pt-4 pb-2">
                                        <div className="relative flex items-start justify-between">
                                            {/* connecting line */}
                                            <div className="absolute top-[9px] left-[10px] right-[10px] h-0.5 bg-border z-0" />
                                            <div
                                                className={`absolute top-[9px] left-[10px] h-0.5 bg-primary z-0 transition-all duration-700 ${stepIdx === 0 ? "w-0" :
                                                        stepIdx === 1 ? "w-1/4" :
                                                            stepIdx === 2 ? "w-1/2" :
                                                                stepIdx === 3 ? "w-3/4" :
                                                                    "w-full"
                                                    }`}
                                            />
                                            {progressSteps.map((step, idx) => (
                                                <div key={step} className="relative z-10 flex flex-col items-center gap-1.5 w-1/5">
                                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${idx <= stepIdx
                                                        ? "bg-primary border-primary"
                                                        : "bg-card border-border"
                                                        }`}>
                                                        {idx < stepIdx && (
                                                            <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                            </svg>
                                                        )}
                                                        {idx === stepIdx && <div className="w-2 h-2 rounded-full bg-white" />}
                                                    </div>
                                                    <p className={`text-[8px] font-semibold text-center leading-tight ${idx <= stepIdx ? "text-primary" : "text-muted-foreground"}`}>
                                                        {step.replace(" for ", "\nfor ")}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* ── Items ── */}
                                    {foodItemsArr.length > 0 && (
                                        <div className="px-4 py-3 border-t border-border space-y-1.5">
                                            {foodItemsArr.slice(0, 3).map((fi, idx) => (
                                                <div key={idx} className="flex justify-between items-center text-sm">
                                                    <span className="text-foreground font-medium">
                                                        {fi.name}
                                                        <span className="text-muted-foreground font-normal ml-1">×{fi.qty}</span>
                                                    </span>
                                                    <span className="text-muted-foreground">₹{(fi.price * fi.qty).toFixed(2)}</span>
                                                </div>
                                            ))}
                                            {foodItemsArr.length > 3 && (
                                                <p className="text-xs text-muted-foreground">+{foodItemsArr.length - 3} more items</p>
                                            )}
                                        </div>
                                    )}

                                    {/* ── Footer ── */}
                                    <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/20">
                                        <div>
                                            <p className="text-xs text-muted-foreground">Total</p>
                                            <p className="font-bold text-foreground text-sm">₹{order.totalAmountPaid?.toFixed(2) ?? "—"}</p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {order.cashOnDelivery && (
                                                <span className="text-xs text-orange-600 bg-orange-50 dark:bg-orange-900/20 px-2 py-1 rounded-full font-semibold">
                                                    💵 COD
                                                </span>
                                            )}
                                            {order.type && (
                                                <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full flex items-center gap-1">
                                                    {order.type === "Delivery" ? <Bike className="w-3 h-3" /> : <Package className="w-3 h-3" />}
                                                    {order.type}
                                                </span>
                                            )}
                                            <button
                                                onClick={() => router.push(`/order/${order.id}`)}
                                                className="flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                                            >
                                                Details <ChevronRight className="w-3 h-3" />
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </AnimatePresence>
                )}
            </div>
        </div>
    );
};

export default function ActiveOrdersPage() {
    return (
        <Suspense fallback={<div className="flex items-center justify-center min-h-[50vh]">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>}>
            <ActiveOrdersContent />
        </Suspense>
    );
}
